import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const EmployeeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}employee/${id}`);
        setEmployee(response.data);
        setLoading(false);
      } catch (err) {
        setError('Error fetching employee details');
        setLoading(false);
        console.error('Fetch error:', err);
      }
    };
    fetchEmployee();
  }, [id]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toISOString().slice(0, 10);
    } catch {
      return 'Invalid Date';
    }
  };

  const renderUrl = (url, label, isImage = false) => {
    if (!url) return <p><strong>{label}:</strong> N/A</p>;
    if (isImage) {
      return (
        <div>
          <strong>{label}:</strong>
          <img src={url} alt={label} className="w-24 h-24 object-cover rounded mt-2" onError={(e) => e.target.style.display = 'none'} />
        </div>
      );
    }
    return (
      <p>
        <strong>{label}:</strong> <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{url}</a>
      </p>
    );
  };

  if (loading) {
    return <div className="flex justify-center p-6"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div></div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  if (!employee) {
    return <div className="p-6">No employee found</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">{employee.firstName} {employee.lastName}</h1>
      <button
        onClick={() => navigate(-1)}
        className="mb-4 bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
      >
        Back
      </button>
      <div className="grid gap-2">
        {/* Employee Details */}
        <h3 className="text-lg font-semibold mt-4">Employee Details</h3>
        <p><strong>Email:</strong> {employee.email || 'N/A'}</p>
        <p><strong>Gender:</strong> {employee.gender || 'N/A'}</p>
        {employee.middleName && <p><strong>Middle Name:</strong> {employee.middleName}</p>}
        {employee.dateOfBirth && <p><strong>Date of Birth:</strong> {formatDate(employee.dateOfBirth)}</p>}
        {employee.address && <p><strong>Address:</strong> {employee.address}</p>}
        {employee.TINType && <p><strong>TIN Type:</strong> {employee.TINType}</p>}
        {employee.TIN !== 0 && <p><strong>TIN:</strong> {employee.TIN}</p>}
        {employee.phoneNumber && <p><strong>Phone:</strong> {employee.phoneNumber}</p>}
        {employee.whatsappNumber && <p><strong>WhatsApp:</strong> {employee.whatsappNumber}</p>}
        <p><strong>Status:</strong> {employee.status || 'N/A'}</p>
        {employee.terminationReason && <p><strong>Termination Reason:</strong> {employee.terminationReason}</p>}
        <p><strong>Distance to Employer:</strong> {employee.distance || 'N/A'}</p>
        <p><strong>Created At:</strong> {formatDate(employee.createdAt)}</p>
        <p><strong>Updated At:</strong> {formatDate(employee.updatedAt)}</p>

        {/* Salary Details */}
        <h3 className="text-lg font-semibold mt-4">Salary Details</h3>
        <p><strong>Gross Salary:</strong> {employee.salary?.gross || 'N/A'}</p>
        <p><strong>Net Salary:</strong> {employee.salary?.net || 'N/A'}</p>
        <h4 className="font-medium">Deductions:</h4>
        <ul className="list-disc pl-5">
          <li>Tax: {employee.salary?.deductions?.tax || 'N/A'}</li>
          <li>Penalty: {employee.salary?.deductions?.penalty || 'N/A'}</li>
          <li>HMO: {employee.salary?.deductions?.hmo || 'N/A'}</li>
          <li>Other: {employee.salary?.deductions?.other || 'N/A'}</li>
        </ul>

        {/* Salary History */}
        <h3 className="text-lg font-semibold mt-4">Salary History</h3>
        {employee.salary?.history?.length > 0 ? (
          <ul className="list-disc pl-5">
            {employee.salary.history.map((hist, index) => (
              <li key={index}>
                {hist.month} {hist.year}: Gross {hist.grossSalary}, Net {hist.netSalary}
                <ul className="list-circle pl-5">
                  <li>Tax: {hist.deductions.tax}</li>
                  <li>Penalty: {hist.deductions.penalty}</li>
                  <li>HMO: {hist.deductions.hmo}</li>
                  <li>Other: {hist.deductions.other}</li>
                </ul>
              </li>
            ))}
          </ul>
        ) : (
          <p>No salary history available.</p>
        )}

        {/* Leave Requests */}
        <h3 className="text-lg font-semibold mt-4">Leave Requests</h3>
        {employee.leaveRequests?.length > 0 ? (
          <ul className="list-disc pl-5">
            {employee.leaveRequests.map((leave, index) => (
              <li key={index}>
                {formatDate(leave.startDate)} to {formatDate(leave.endDate)}: {leave.reason} ({leave.status})
                {leave.others && <p>Others: {leave.others}</p>}
                {leave.response && <p>Response: {leave.response}</p>}
              </li>
            ))}
          </ul>
        ) : (
          <p>No leave requests available.</p>
        )}

        {/* Queries */}
        <h3 className="text-lg font-semibold mt-4">Queries</h3>
        {employee.queries?.length > 0 ? (
          <ul className="list-disc pl-5">
            {employee.queries.map((query, index) => (
              <li key={index}>
                {formatDate(query.dateIssued)}: {query.description}
                {query.response && <p>Response: {query.response} ({formatDate(query.responseDate)})</p>}
              </li>
            ))}
          </ul>
        ) : (
          <p>No queries available.</p>
        )}

        {/* Attendance */}
        <h3 className="text-lg font-semibold mt-4">Attendance</h3>
        {employee.attendance?.length > 0 ? (
          <ul className="list-disc pl-5">
            {employee.attendance.map((att, index) => (
              <li key={index}>
                {formatDate(att.date)}: {att.status}
                {att.notes && <p>Notes: {att.notes}</p>}
              </li>
            ))}
          </ul>
        ) : (
          <p>No attendance records available.</p>
        )}

        {/* Remarks */}
        <h3 className="text-lg font-semibold mt-4">Remarks</h3>
        {employee.remarks?.length > 0 ? (
          <ul className="list-disc pl-5">
            {employee.remarks.map((remark, index) => (
              <li key={index}>
                {remark.month} {remark.year}: {remark.comment}
              </li>
            ))}
          </ul>
        ) : (
          <p>No remarks available.</p>
        )}

        {/* Employer Details */}
        {employee.employer && (
          <>
            <h3 className="text-lg font-semibold mt-4">Employer Details</h3>
            <p><strong>Company Name:</strong> {employee.employer.companyName || 'N/A'}</p>
            <p><strong>Email:</strong> {employee.employer.email || 'N/A'}</p>
            <p><strong>Address:</strong> {employee.employer.address || 'N/A'}</p>
            <p><strong>State:</strong> {employee.employer.state || 'N/A'}</p>
            <p><strong>Local Government:</strong> {employee.employer.localGovernment || 'N/A'}</p>
            <p><strong>Country:</strong> {employee.employer.country || 'N/A'}</p>
            <p><strong>Phone Number:</strong> {employee.employer.phoneNumber || 'N/A'}</p>
            <p><strong>Employer Type:</strong> {employee.employer.employerType || 'N/A'}</p>
            <p><strong>User Type:</strong> {employee.employer.userType || 'N/A'}</p>
            <p><strong>Unique Number:</strong> {employee.employer.uniqueNumber || 'N/A'}</p>
            <p><strong>Is Verified:</strong> {employee.employer.isVerified ? 'Yes' : 'No'}</p>
            <p><strong>Is Blacklisted:</strong> {employee.employer.isBlacklisted ? 'Yes' : 'No'}</p>
            <p><strong>Last Login:</strong> {formatDate(employee.employer.lastLogin)}</p>
            <p><strong>Created At:</strong> {formatDate(employee.employer.createdAt)}</p>
            <p><strong>Updated At:</strong> {formatDate(employee.employer.updatedAt)}</p>
            {renderUrl(employee.employer.avatar, 'Avatar', true)}
            {employee.employer.size && <p><strong>Company Size:</strong> {employee.employer.size}</p>}
            {employee.employer.listedJobs?.length > 0 && (
              <p><strong>Listed Jobs:</strong> {employee.employer.listedJobs.length} job(s)</p>
            )}
            {employee.employer.jobOffers?.length > 0 && (
              <p><strong>Job Offers:</strong> {employee.employer.jobOffers.length} offer(s)</p>
            )}
            {employee.employer.notifications?.length > 0 && (
              <p><strong>Notifications:</strong> {employee.employer.notifications.length} notification(s)</p>
            )}
            {employee.employer.reportsMade?.length > 0 && (
              <p><strong>Reports Made:</strong> {employee.employer.reportsMade.length} report(s)</p>
            )}
            {employee.employer.reportsGotten?.length > 0 && (
              <p><strong>Reports Gotten:</strong> {employee.employer.reportsGotten.length} report(s)</p>
            )}
            {employee.employer.servicesRendered?.length > 0 && (
              <p><strong>Services Rendered:</strong> {employee.employer.servicesRendered.length} service(s)</p>
            )}
          </>
        )}

        {/* Job Seeker Details */}
        {employee.jobSeeker && (
          <>
            <h3 className="text-lg font-semibold mt-4">Job Seeker Details</h3>
            <p><strong>First Name:</strong> {employee.jobSeeker.firstName || 'N/A'}</p>
            <p><strong>Last Name:</strong> {employee.jobSeeker.lastName || 'N/A'}</p>
            <p><strong>Email:</strong> {employee.jobSeeker.email || 'N/A'}</p>
            <p><strong>Gender:</strong> {employee.jobSeeker.gender || 'N/A'}</p>
            <p><strong>Address:</strong> {employee.jobSeeker.address || 'N/A'}</p>
            <p><strong>State:</strong> {employee.jobSeeker.state || 'N/A'}</p>
            <p><strong>Local Government:</strong> {employee.jobSeeker.localGovernment || 'N/A'}</p>
            <p><strong>Country:</strong> {employee.jobSeeker.country || 'N/A'}</p>
            <p><strong>Phone Number:</strong> {employee.jobSeeker.phoneNumber || 'N/A'}</p>
            <p><strong>User Type:</strong> {employee.jobSeeker.userType || 'N/A'}</p>
            <p><strong>Unique Number:</strong> {employee.jobSeeker.uniqueNumber || 'N/A'}</p>
            <p><strong>Is Verified:</strong> {employee.jobSeeker.isVerified ? 'Yes' : 'No'}</p>
            <p><strong>Is Blacklisted:</strong> {employee.jobSeeker.isBlacklisted ? 'Yes' : 'No'}</p>
            <p><strong>Is NIN Verified:</strong> {employee.jobSeeker.isNINVerified ? 'Yes' : 'No'}</p>
            <p><strong>Last Login:</strong> {formatDate(employee.jobSeeker.lastLogin)}</p>
            <p><strong>Created At:</strong> {formatDate(employee.jobSeeker.createdAt)}</p>
            <p><strong>Updated At:</strong> {formatDate(employee.jobSeeker.updatedAt)}</p>
            {renderUrl(employee.jobSeeker.avatar, 'Avatar', true)}
            {employee.jobSeeker.dateOfBirth && (
              <p><strong>Date of Birth:</strong> {formatDate(employee.jobSeeker.dateOfBirth)}</p>
            )}
            {employee.jobSeeker.whatsappNumber && (
              <p><strong>WhatsApp:</strong> {employee.jobSeeker.whatsappNumber}</p>
            )}
            {employee.jobSeeker.anyChild && <p><strong>Any Children:</strong> {employee.jobSeeker.anyChild}</p>}
            {employee.jobSeeker.criminalRecord && (
              <p><strong>Criminal Record:</strong> {employee.jobSeeker.criminalRecord}</p>
            )}
            {employee.jobSeeker.handworkSkill && (
              <p><strong>Handwork Skill:</strong> {employee.jobSeeker.handworkSkill}</p>
            )}
            {employee.jobSeeker.specifyHandworkSkill && (
              <p><strong>Specify Handwork Skill:</strong> {employee.jobSeeker.specifyHandworkSkill}</p>
            )}
            {employee.jobSeeker.instagramHandle && (
              <p><strong>Instagram Handle:</strong> {employee.jobSeeker.instagramHandle}</p>
            )}
            {employee.jobSeeker.tiktokHandle && (
              <p><strong>TikTok Handle:</strong> {employee.jobSeeker.tiktokHandle}</p>
            )}
            {employee.jobSeeker.jobDecision && (
              <p><strong>Job Decision:</strong> {employee.jobSeeker.jobDecision}</p>
            )}
            {employee.jobSeeker.jobDecisionSalary && (
              <p><strong>Job Decision Salary:</strong> {employee.jobSeeker.jobDecisionSalary}</p>
            )}
            {employee.jobSeeker.leastSalary && (
              <p><strong>Least Salary:</strong> {employee.jobSeeker.leastSalary}</p>
            )}
            {employee.jobSeeker.minimumSalary && (
              <p><strong>Minimum Salary:</strong> {employee.jobSeeker.minimumSalary}</p>
            )}
            {employee.jobSeeker.landlordNumber && (
              <p><strong>Landlord Number:</strong> {employee.jobSeeker.landlordNumber}</p>
            )}
            {employee.jobSeeker.liveAlone && <p><strong>Live Alone:</strong> {employee.jobSeeker.liveAlone}</p>}
            {employee.jobSeeker.whoDoYouLiveWith && (
              <p><strong>Who Do You Live With:</strong> {employee.jobSeeker.whoDoYouLiveWith}</p>
            )}
            {employee.jobSeeker.numberOfChildren && (
              <p><strong>Number of Children:</strong> {employee.jobSeeker.numberOfChildren}</p>
            )}
            {employee.jobSeeker.numberOfPeople && (
              <p><strong>Number of People:</strong> {employee.jobSeeker.numberOfPeople}</p>
            )}
            {employee.jobSeeker.numberOfRooms && (
              <p><strong>Number of Rooms:</strong> {employee.jobSeeker.numberOfRooms}</p>
            )}
            {employee.jobSeeker.registeredEverify && (
              <p><strong>Registered E-Verify:</strong> {employee.jobSeeker.registeredEverify}</p>
            )}
            {employee.jobSeeker.bankAcctName && (
              <p><strong>Bank Account Name:</strong> {employee.jobSeeker.bankAcctName}</p>
            )}
            {employee.jobSeeker.bankAcctNum && (
              <p><strong>Bank Account Number:</strong> {employee.jobSeeker.bankAcctNum}</p>
            )}
            {employee.jobSeeker.bankName && <p><strong>Bank Name:</strong> {employee.jobSeeker.bankName}</p>}
            {employee.jobSeeker.nextOfKin && (
              <p><strong>Next of Kin:</strong> {employee.jobSeeker.nextOfKin}</p>
            )}
            {employee.jobSeeker.nextOfKinAddress && (
              <p><strong>Next of Kin Address:</strong> {employee.jobSeeker.nextOfKinAddress}</p>
            )}
            {employee.jobSeeker.nextOfKinNumber && (
              <p><strong>Next of Kin Number:</strong> {employee.jobSeeker.nextOfKinNumber}</p>
            )}
            {employee.jobSeeker.nextOfKinRelationship && (
              <p><strong>Next of Kin Relationship:</strong> {employee.jobSeeker.nextOfKinRelationship}</p>
            )}
            {employee.jobSeeker.nin && <p><strong>NIN:</strong> {employee.jobSeeker.nin}</p>}
            {renderUrl(employee.jobSeeker.residentialCard, 'Residential Card')}
            {renderUrl(employee.jobSeeker.scanningEvidence, 'Scanning Evidence')}
            {renderUrl(employee.jobSeeker.tenancyAgreement, 'Tenancy Agreement')}
            {renderUrl(employee.jobSeeker.votersCard, "Voter's Card")}
            {employee.jobSeeker.jobMode && <p><strong>Job Mode:</strong> {employee.jobSeeker.jobMode}</p>}
            {employee.jobSeeker.availabilityForUrgentWork && (
              <p><strong>Availability for Urgent Work:</strong> {employee.jobSeeker.availabilityForUrgentWork ? 'Yes' : 'No'}</p>
            )}
            {employee.jobSeeker.isCertified && (
              <p><strong>Is Certified:</strong> {employee.jobSeeker.isCertified ? 'Yes' : 'No'}</p>
            )}
            {employee.jobSeeker.certificationDate && (
              <p><strong>Certification Date:</strong> {formatDate(employee.jobSeeker.certificationDate)}</p>
            )}
            {employee.jobSeeker.educationLevel && (
              <p><strong>Education Level:</strong> {employee.jobSeeker.educationLevel}</p>
            )}
            {employee.jobSeeker.maritalStatus && (
              <p><strong>Marital Status:</strong> {employee.jobSeeker.maritalStatus}</p>
            )}
            {employee.jobSeeker.accountBalance !== undefined && (
              <p><strong>Account Balance:</strong> {employee.jobSeeker.accountBalance}</p>
            )}

            {/* Education Records */}
            <h3 className="text-lg font-semibold mt-4">Education Records</h3>
            {employee.jobSeeker.education?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.education.map((edu, index) => (
                  <li key={index}>
                    {edu.institution} ({edu.startYear} - {edu.endYear || 'Present'}): {edu.degree} in {edu.field}
                    {edu.description && <p>Description: {edu.description}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No education records available.</p>
            )}

            {/* Work Experience */}
            <h3 className="text-lg font-semibold mt-4">Work Experience</h3>
            {employee.jobSeeker.workExperience?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.workExperience.map((exp, index) => (
                  <li key={index}>
                    {exp.company} ({exp.startDate} - {exp.endDate || 'Present'}): {exp.position}
                    {exp.description && <p>Description: {exp.description}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No work experience available.</p>
            )}

            {/* Certificates */}
            <h3 className="text-lg font-semibold mt-4">Certificates</h3>
            {employee.jobSeeker.certificates?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.certificates.map((cert, index) => (
                  <li key={index}>
                    {cert.name} ({cert.issuer}, {formatDate(cert.date)}): {cert.description || 'N/A'}
                    {cert.url && <p><a href={cert.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">View Certificate</a></p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No certificates available.</p>
            )}

            {/* Guarantors */}
            <h3 className="text-lg font-semibold mt-4">Guarantors</h3>
            {employee.jobSeeker.guarantors?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.guarantors.map((guarantor, index) => (
                  <li key={index}>
                    {guarantor.name}: {guarantor.relationship} ({guarantor.phoneNumber})
                    {guarantor.address && <p>Address: {guarantor.address}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No guarantors available.</p>
            )}

            {/* Hired History */}
            <h3 className="text-lg font-semibold mt-4">Hired History</h3>
            {employee.jobSeeker.hiredHistory?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.hiredHistory.map((hire, index) => (
                  <li key={index}>
                    {hire.company} ({formatDate(hire.startDate)} - {formatDate(hire.endDate) || 'Present'}): {hire.position}
                    {hire.description && <p>Description: {hire.description}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No hired history available.</p>
            )}

            {/* Reports Made */}
            <h3 className="text-lg font-semibold mt-4">Reports Made</h3>
            {employee.jobSeeker.reportsMade?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.reportsMade.map((report, index) => (
                  <li key={index}>
                    {formatDate(report.date)}: {report.description}
                    {report.status && <p>Status: {report.status}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No reports made available.</p>
            )}

            {/* Reports Gotten */}
            <h3 className="text-lg font-semibold mt-4">Reports Gotten</h3>
            {employee.jobSeeker.reportsGotten?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.reportsGotten.map((report, index) => (
                  <li key={index}>
                    {formatDate(report.date)}: {report.description}
                    {report.status && <p>Status: {report.status}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No reports gotten available.</p>
            )}

            {/* Benefits */}
            <h3 className="text-lg font-semibold mt-4">Benefits</h3>
            {employee.jobSeeker.benefits?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.benefits.map((benefit, index) => (
                  <li key={index}>
                    {benefit.name}: {benefit.description || 'N/A'}
                    {benefit.date && <p>Date: {formatDate(benefit.date)}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No benefits available.</p>
            )}

            {/* Job Offers */}
            <h3 className="text-lg font-semibold mt-4">Job Offers</h3>
            {employee.jobSeeker.jobOffers?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.jobOffers.map((offer, index) => (
                  <li key={index}>
                    {offer.jobTitle} at {offer.company} ({formatDate(offer.offerDate)}): {offer.status}
                    {offer.salary && <p>Salary: {offer.salary}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No job offers available.</p>
            )}

            {/* Applied Jobs */}
            <h3 className="text-lg font-semibold mt-4">Applied Jobs</h3>
            {employee.jobSeeker.appliedJobs?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.appliedJobs.map((job, index) => (
                  <li key={index}>
                    {job.jobTitle} at {job.company} ({formatDate(job.applicationDate)}): {job.status}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No applied jobs available.</p>
            )}

            {/* Interested Jobs */}
            <h3 className="text-lg font-semibold mt-4">Interested Jobs</h3>
            {employee.jobSeeker.interestedJob?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.interestedJob.map((job, index) => (
                  <li key={index}>
                    {job.jobTitle} at {job.company} ({formatDate(job.interestDate)}): {job.category || 'N/A'}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No interested jobs available.</p>
            )}

            {/* Resumes */}
            <h3 className="text-lg font-semibold mt-4">Resumes</h3>
            {employee.jobSeeker.resume?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.resume.map((res, index) => (
                  <li key={index}>
                    Resume {index + 1}
                    {res.url && <p><a href={res.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">View Resume</a></p>}
                    {res.uploadDate && <p>Uploaded: {formatDate(res.uploadDate)}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No resumes available.</p>
            )}

            {/* Job Choices */}
            <h3 className="text-lg font-semibold mt-4">Job Choices</h3>
            {employee.jobSeeker.jobChoice?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.jobChoice.map((choice, index) => (
                  <li key={index}>
                    {choice.category}: {choice.description || 'N/A'}
                    {choice.date && <p>Date: {formatDate(choice.date)}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No job choices available.</p>
            )}

            {/* Services Rendered */}
            <h3 className="text-lg font-semibold mt-4">Services Rendered</h3>
            {employee.jobSeeker.servicesRendered?.length > 0 ? (
              <ul className="list-disc pl-5">
                {employee.jobSeeker.servicesRendered.map((service, index) => (
                  <li key={index}>
                    {service.name}: {service.description || 'N/A'}
                    {service.date && <p>Date: {formatDate(service.date)}</p>}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No services rendered available.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeDetails;